import { useCallback, useEffect, useRef, useState } from 'react';
import SavedViewsApi, {
  LegacySavedView,
  NewSavedView,
  SavedViewRecord,
  SavedViewReportKey,
} from '../services/reports/SavedViews.service';

type ScopeId = string | number | null | undefined;

// A view as a report kept it in this browser, ready to import. isFavorite carries the old star.
export interface LocalSavedView<P> extends LegacySavedView<P> {
  isFavorite?: boolean;
}

export type SavedViewsStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface UseSavedViewsOptions<P> {
  reportKey: SavedViewReportKey;
  clientId: ScopeId;
  userId: ScopeId;
  // Prefix of this report's browser storage keys, e.g. 'vendor-performance'.
  storagePrefix: string;
  // The views this user kept in this browser for this client, already filtered to valid ones.
  readLocalViews: () => LocalSavedView<P>[];
}

export interface UseSavedViewsResult<P> {
  views: SavedViewRecord<P>[];
  status: SavedViewsStatus;
  // True while the server is unreachable and the list shows this browser's views read-only.
  isLocalFallback: boolean;
  saveView: (view: NewSavedView<P>) => Promise<SavedViewRecord<P>>;
  deleteView: (id: number) => Promise<void>;
  isFavorite: (id: number) => boolean;
  toggleFavorite: (id: number) => void;
}

const hasScope = (id: ScopeId): id is string | number => id !== null && id !== undefined && id !== '';

export const getSavedViewsImportedKey = (storagePrefix: string, userId: string | number, clientId: string | number): string =>
  `${storagePrefix}-saved-views-imported-${userId}-${clientId}`;

export const getSavedViewFavoritesKey = (storagePrefix: string, userId: ScopeId, clientId: string | number): string =>
  `${storagePrefix}-saved-view-favorites-${userId ?? 'anonymous'}-${clientId}`;

const readStorage = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (key: string, value: string): void => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage can be unavailable (private mode, quota); favorites then last for the session only.
  }
};

const readFavoriteIds = (key: string): number[] => {
  try {
    const parsed = JSON.parse(readStorage(key) ?? '[]');
    return Array.isArray(parsed) ? parsed.filter((id): id is number => typeof id === 'number') : [];
  } catch {
    return [];
  }
};

// Browser-only views shown while the server is unreachable: negative ids never collide with saved
// ones, and they can be applied but not changed.
const toFallbackRecords = <P>(reportKey: SavedViewReportKey, local: LocalSavedView<P>[]): SavedViewRecord<P>[] =>
  local.map((view, index) => ({
    id: -(index + 1),
    reportKey,
    name: view.name,
    visibility: 'only_me',
    payload: view.payload,
    schemaVersion: 1,
    legacyId: view.legacyId,
    ownerName: null,
    canEdit: false,
    createdAt: null,
    updatedAt: null,
  }));

/**
 * A report's saved views, stored on the server per user and client.
 *
 * On first load for a user + client, views this browser still holds from before views were stored
 * on the server are imported once (as private views); the browser copy is left in place. Stars are
 * a per-user preference and stay in this browser.
 */
export function useSavedViews<P>({
  reportKey,
  clientId,
  userId,
  storagePrefix,
  readLocalViews,
}: UseSavedViewsOptions<P>): UseSavedViewsResult<P> {
  const [views, setViews] = useState<SavedViewRecord<P>[]>([]);
  const [status, setStatus] = useState<SavedViewsStatus>('idle');
  const [isLocalFallback, setIsLocalFallback] = useState(false);
  const favoritesKey = hasScope(clientId) ? getSavedViewFavoritesKey(storagePrefix, userId, clientId) : null;
  const [favoriteIds, setFavoriteIds] = useState<number[]>(() => (favoritesKey ? readFavoriteIds(favoritesKey) : []));

  // Read through a ref so a new function identity each render doesn't reload the list.
  const readLocalViewsRef = useRef(readLocalViews);
  readLocalViewsRef.current = readLocalViews;

  useEffect(() => {
    setFavoriteIds(favoritesKey ? readFavoriteIds(favoritesKey) : []);
  }, [favoritesKey]);

  useEffect(() => {
    if (!hasScope(clientId)) {
      setViews([]);
      setStatus('idle');
      setIsLocalFallback(false);
      return undefined;
    }

    let active = true;
    setStatus('loading');

    const load = async () => {
      const local = readLocalViewsRef.current();
      let list: SavedViewRecord<P>[];
      try {
        list = await SavedViewsApi.list<P>(clientId, reportKey);
      } catch {
        if (!active) return;
        setViews(toFallbackRecords(reportKey, local));
        setIsLocalFallback(local.length > 0);
        setStatus('error');
        return;
      }

      // Only a signed-in user's own browser views are imported, once per user + client.
      const importedKey = hasScope(userId) ? getSavedViewsImportedKey(storagePrefix, userId, clientId) : null;
      if (importedKey && readStorage(importedKey) === null) {
        if (local.length === 0) {
          writeStorage(importedKey, new Date().toISOString());
        } else {
          try {
            const result = await SavedViewsApi.importLegacy<P>(clientId, reportKey, local);
            list = result.views;
            writeStorage(importedKey, new Date().toISOString());
            const starred = new Set(local.filter((view) => view.isFavorite).map((view) => view.legacyId));
            const starredIds = list.filter((view) => view.legacyId && starred.has(view.legacyId)).map((view) => view.id);
            if (starredIds.length > 0 && favoritesKey) {
              const merged = Array.from(new Set([...readFavoriteIds(favoritesKey), ...starredIds]));
              writeStorage(favoritesKey, JSON.stringify(merged));
              if (active) setFavoriteIds(merged);
            }
          } catch {
            // Not marked as imported, so the next load tries again; the list loaded above still shows.
          }
        }
      }

      if (!active) return;
      setViews(list);
      setIsLocalFallback(false);
      setStatus('ready');
    };

    load();
    return () => {
      active = false;
    };
  }, [clientId, userId, reportKey, storagePrefix, favoritesKey]);

  const saveView = useCallback(
    async (view: NewSavedView<P>) => {
      if (!hasScope(clientId)) throw new Error('A client is required to save a view.');
      const created = await SavedViewsApi.create<P>(clientId, reportKey, view);
      setViews((prev) => [...prev, created]);
      setIsLocalFallback(false);
      return created;
    },
    [clientId, reportKey]
  );

  const deleteView = useCallback(async (id: number) => {
    await SavedViewsApi.remove(id);
    setViews((prev) => prev.filter((view) => view.id !== id));
  }, []);

  const isFavorite = useCallback((id: number) => favoriteIds.includes(id), [favoriteIds]);

  const toggleFavorite = useCallback(
    (id: number) => {
      setFavoriteIds((prev) => {
        const next = prev.includes(id) ? prev.filter((existing) => existing !== id) : [...prev, id];
        if (favoritesKey) writeStorage(favoritesKey, JSON.stringify(next));
        return next;
      });
    },
    [favoritesKey]
  );

  return { views, status, isLocalFallback, saveView, deleteView, isFavorite, toggleFavorite };
}
